import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { Snackbar, Alert } from '@mui/material';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import moment from 'moment';

import {
  GetAllCampainDatanotactivate,
  UpdateOneCampaignData,
} from '../../Redux/Actions/Campaign_Actions';
import { Notify }          from '../../Utility/Notify';
import { useSidebarWidth } from '../../Utility/useSidebarWidth';

/* ─── Accent ──────────────────────────────────────────────────────────────── */
const ACCENT      = '#0D47A1';
const ACCENT_DARK = '#0a3880';
const ACCENT_SOFT = 'rgba(13,71,161,0.09)';

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const IconSearch   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>;
const IconCopy     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>;
const IconRestore  = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.5"/></svg>;
const IconClose    = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IconWarning  = () => <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
const IconMega     = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>;
const IconCoin     = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>;
const IconTrash    = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>;
const IconTrend    = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;

/* ─── Portal Confirm Modal ────────────────────────────────────────────────── */
const PortalModal = ({ isDark, title, subtitle, onClose, onConfirm, confirmLabel, confirmColor, loading }) =>
  createPortal(
    <div
      style={{ position:'fixed',inset:0,zIndex:999999,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.75)',backdropFilter:'blur(10px)',animation:'pm-fade 0.2s ease both' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ width:'100%',maxWidth:'480px',margin:'0 20px',borderRadius:'24px',padding:'36px 32px 32px',background:isDark?'linear-gradient(135deg,#1a1f2e,#141829)':'#fff',boxShadow:'0 40px 100px rgba(0,0,0,0.35)',position:'relative',animation:'pm-slide 0.26s cubic-bezier(0.34,1.2,0.64,1) both',color:isDark?'#E4E8F5':'#1C2340' }}>
        <button onClick={onClose} style={{ position:'absolute',top:16,right:16,width:34,height:34,borderRadius:'10px',border:`1px solid ${isDark?'#3A4570':'#E8EAF0'}`,background:isDark?'#252D4A':'#F7F8FC',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:isDark?'#9099c0':'#7B829A' }}>
          <IconClose />
        </button>
        <div style={{ width:76,height:76,borderRadius:'50%',background:`linear-gradient(135deg,${confirmColor}22,${confirmColor}44)`,border:`2px solid ${confirmColor}66`,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 20px',color:confirmColor }}>
          <IconWarning />
        </div>
        <p style={{ fontSize:'20px',fontWeight:700,textAlign:'center',margin:'0 0 8px',letterSpacing:'-0.3px',fontFamily:"'DM Sans',sans-serif" }}>{title}</p>
        <p style={{ fontSize:'13px',color:isDark?'#7B829A':'#6b7280',textAlign:'center',margin:'0 0 28px',lineHeight:1.6,fontFamily:"'DM Sans',sans-serif" }}>{subtitle}</p>
        <div style={{ display:'flex',gap:'10px' }}>
          <button onClick={onClose} disabled={loading} style={{ flex:1,padding:'13px',borderRadius:'12px',border:`1.5px solid ${isDark?'#3A4570':'#E8EAF0'}`,background:isDark?'#252D4A':'#F7F8FC',color:isDark?'#E4E8F5':'#1C2340',fontFamily:"'DM Sans',sans-serif",fontSize:'14px',fontWeight:600,cursor:'pointer' }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading} style={{ flex:1,padding:'13px',borderRadius:'12px',border:'none',background:`linear-gradient(135deg,${confirmColor},${confirmColor}cc)`,color:'#fff',fontFamily:"'DM Sans',sans-serif",fontSize:'14px',fontWeight:700,cursor:loading?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'8px',opacity:loading?0.7:1 }}>
            {loading
              ? <><div style={{ width:16,height:16,border:'2px solid rgba(255,255,255,0.3)',borderTopColor:'#fff',borderRadius:'50%',animation:'pm-spin 0.7s linear infinite' }}/>Processing…</>
              : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );

/* ─── Main Component ─────────────────────────────────────────────────────── */
export const AllCampaignTrash = () => {
  const dispatch     = useDispatch();
  const theme        = useTheme();
  const isDark       = theme.palette.mode === 'dark';
  const sidebarWidth = useSidebarWidth();

  const [loading,        setLoading]        = useState(true);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [searchTerm,     setSearchTerm]     = useState('');
  const [page,           setPage]           = useState(1);
  const [itemsPerPage,   setItemsPerPage]   = useState(10);
  const [selectedItems,  setSelectedItems]  = useState([]);
  const [copyOpen,       setCopyOpen]       = useState(false);
  const [confirmModal,   setConfirmModal]   = useState({ open:false, id:null, name:'' });
  const [userlogin,      setUserlogin]      = useState(null);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) setUserlogin(JSON.parse(u));
  }, []);

  const fetchData = async () => {
    setLoading(true);
    await dispatch(GetAllCampainDatanotactivate());
    setLoading(false);
  };
  useEffect(() => { fetchData(); }, []);

  const res        = useSelector(s => s.AllCampaign.campaign);
  const allItems   = Array.isArray(res?.data) ? res.data : [];

  /* filter */
  const filtered = allItems.filter(item => {
    const q = searchTerm.toLowerCase();
    return !q || item.CampainName?.toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const pageItems  = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  /* stats */
  const thisMonth  = allItems.filter(i => moment(i.createdAt).isAfter(moment().subtract(30,'days'))).length;
  const totalCost  = allItems.reduce((sum, i) => sum + (parseFloat(i.Cost) || 0), 0);

  /* home route */
  const homeRoute = userlogin?.data?.role === 'Admin'
    ? '/AllLeadsForAdmin'
    : userlogin?.data?.role === 'Marketer'
    ? '/LeadsForMarketer'
    : '/';

  /* checkbox */
  const isAllSelected  = pageItems.length > 0 && pageItems.every(i => selectedItems.includes(i._id));
  const isSomeSelected = selectedItems.length > 0 && !isAllSelected;
  const handleSelectAll = e => {
    if (e.target.checked) setSelectedItems(pageItems.map(i => i._id));
    else setSelectedItems([]);
  };
  const handleSelectOne = id =>
    setSelectedItems(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  /* restore single */
  const openConfirm = item => setConfirmModal({ open:true, id:item._id, name:item.CampainName });

  /* restore confirm */
  const handleRestoreConfirm = async () => {
    setRestoreLoading(true);
    await dispatch(UpdateOneCampaignData(confirmModal.id, { campaignisactivatedelete:'true' }));
    setRestoreLoading(false);
    setConfirmModal({ open:false, id:null, name:'' });
    Notify('success', 'Campaign restored successfully', 'success');
    fetchData();
  };

  /* bulk restore */
  const handleBulkRestore = async () => {
    setRestoreLoading(true);
    for (const id of selectedItems) {
      await dispatch(UpdateOneCampaignData(id, { campaignisactivatedelete:'true' }));
    }
    setRestoreLoading(false);
    Notify('success', `${selectedItems.length} campaign(s) restored successfully`, 'success');
    setSelectedItems([]);
    fetchData();
  };

  /* pagination numbers */
  const buildPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length:totalPages }, (_, i) => i + 1);
    const pages = [];
    if (page <= 4)                   pages.push(1,2,3,4,5,'...',totalPages);
    else if (page >= totalPages - 3) pages.push(1,'...',totalPages-4,totalPages-3,totalPages-2,totalPages-1,totalPages);
    else                             pages.push(1,'...',page-1,page,page+1,'...',totalPages);
    return pages;
  };

  /* ── inline CSS ── */
  const css = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');

    @keyframes pm-fade  { from{opacity:0} to{opacity:1} }
    @keyframes pm-slide { from{opacity:0;transform:translateY(24px) scale(0.96)} to{opacity:1;transform:none} }
    @keyframes pm-spin  { to{transform:rotate(360deg)} }

    .ca-shell * { box-sizing:border-box; font-family:'DM Sans',sans-serif; }

    /* ── Stats ── */
    .ca-stats { display:flex; gap:16px; padding:20px 20px 0; flex-wrap:wrap; }
    .ca-stat-card { flex:1; min-width:190px; border-radius:16px; padding:22px 24px; display:flex; justify-content:space-between; align-items:flex-start; border:1px solid #e8eaf0; }
    .ca-stat-card.light { background:#fff; box-shadow:0 1px 4px rgba(16,24,40,.06); }
    .ca-stat-card.dark-card {
      background:linear-gradient(135deg,${ACCENT_DARK},${ACCENT});
      border-color:transparent; box-shadow:0 4px 20px rgba(13,71,161,0.40);
    }
    .ca-stat-label       { font-size:11px; font-weight:600; letter-spacing:.08em; color:#7B829A; text-transform:uppercase; margin-bottom:8px; }
    .ca-stat-label.white { color:rgba(255,255,255,.75); }
    .ca-stat-value       { font-size:32px; font-weight:800; color:#1C2340; line-height:1; letter-spacing:-1px; }
    .ca-stat-value.large { font-size:28px; }
    .ca-stat-value.white { color:#fff; }
    .ca-stat-badge       { display:inline-flex; align-items:center; gap:4px; margin-top:10px; font-size:12px; font-weight:600; padding:3px 10px; border-radius:999px; }
    .ca-stat-badge.blue  { background:${ACCENT_SOFT}; color:${ACCENT}; }
    .ca-stat-badge.amber { background:rgba(245,158,11,0.1); color:#d97706; }
    .ca-badge-pill       { background:rgba(255,255,255,.22); color:#fff; font-size:10px; font-weight:700; padding:3px 10px; border-radius:999px; letter-spacing:.06em; }
    .ca-stat-icon        { width:52px; height:52px; border-radius:14px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
    .ca-stat-icon.light  { background:${ACCENT_SOFT}; color:${ACCENT}; }
    .ca-stat-icon.dark   { background:rgba(255,255,255,.18); color:#fff; }

    .dark .ca-stat-card.light { background:#1a1a2e; border-color:#2a2a45; }
    .dark .ca-stat-label      { color:#6070a0; }
    .dark .ca-stat-value      { color:#e0e0ff; }
    .dark .ca-stat-icon.light { background:rgba(13,71,161,0.25); color:#90b4ff; }

    /* ── Table Section ── */
    .ca-table-section { margin:20px; border-radius:16px; overflow:hidden; border:1px solid #e8eaf0; background:#fff; display:flex; flex-direction:column; }
    .dark .ca-table-section { background:#1a1a2e; border-color:#2a2a45; }

    .ca-table-header { display:flex; align-items:center; justify-content:space-between; padding:20px 24px; border-bottom:1px solid #e8eaf0; flex-wrap:wrap; gap:12px; }
    .dark .ca-table-header { border-color:#2a2a45; }
    .ca-title-label { font-size:10px; font-weight:700; letter-spacing:.12em; color:#7B829A; text-transform:uppercase; margin-bottom:2px; }
    .ca-title       { font-size:18px; font-weight:800; color:#1C2340; letter-spacing:-.3px; }
    .dark .ca-title { color:#e0e0ff; }
    .ca-actions     { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }

    /* search */
    .ca-search { display:flex; align-items:center; gap:8px; padding:9px 14px; border:1.5px solid #e0e2ef; border-radius:10px; background:#f7f8fc; color:#7B829A; min-width:250px; }
    .dark .ca-search { background:#252540; border-color:#3a3a5a; color:#7070a0; }
    .ca-search input { border:none; background:none; outline:none; font-size:13px; color:#1C2340; font-family:'DM Sans',sans-serif; width:100%; }
    .dark .ca-search input { color:#e0e0ff; }
    .ca-search input::placeholder { color:#9ca3af; }

    /* buttons */
    .ca-btn-primary {
      display:flex; align-items:center; gap:6px; padding:9px 16px;
      background:${ACCENT}; border:none; border-radius:8px; cursor:pointer;
      font-size:13px; font-weight:600; color:#fff; font-family:'DM Sans',sans-serif;
      transition:background 0.15s; white-space:nowrap;
    }
    .ca-btn-primary:hover { background:${ACCENT_DARK}; }

    .ca-btn-restore {
      display:flex; align-items:center; gap:6px; padding:9px 14px;
      background:#16a34a; border:none; border-radius:8px; cursor:pointer;
      font-size:13px; font-weight:600; color:#fff; font-family:'DM Sans',sans-serif;
      white-space:nowrap; transition:background 0.15s;
    }
    .ca-btn-restore:hover    { background:#15803d; }
    .ca-btn-restore:disabled { opacity:0.6; cursor:not-allowed; }

    /* ── Table ── */
    .ca-table-wrap { flex:1; overflow:auto; }
    .ca-table { width:100%; border-collapse:collapse; font-size:13px; }
    .ca-table th {
      padding:12px 16px; text-align:left; font-size:10.5px; font-weight:700;
      letter-spacing:.08em; text-transform:uppercase; color:#7B829A;
      border-bottom:2px solid #e8eaf0; white-space:nowrap;
      position:sticky; top:0; z-index:2; background:#fff;
    }
    .dark .ca-table th { border-color:#2a2a45; background:#1a1a2e; }
    .ca-table td { padding:14px 16px; border-bottom:1px solid #f0f2f8; vertical-align:middle; }
    .dark .ca-table td { border-color:#252540; }

    .ca-row { transition:background 0.12s; }
    .ca-row:hover { background:#f5f7ff !important; }
    .dark .ca-row:hover { background:#1e1e3a !important; }
    .ca-row:last-child td { border-bottom:none; }

    /* cells */
    .ca-name-cell { display:flex; align-items:center; gap:10px; }
    .ca-thumb {
      width:38px; height:38px; border-radius:10px; flex-shrink:0;
      background:linear-gradient(135deg,${ACCENT_DARK},${ACCENT});
      display:flex; align-items:center; justify-content:center;
      font-size:13px; font-weight:700; color:#fff; letter-spacing:0.5px;
    }
    .ca-name    { font-size:14px; font-weight:600; color:#1C2340; }
    .dark .ca-name { color:#e0e0ff; }
    .ca-id-copy { font-size:11px; color:${ACCENT}; cursor:pointer; }
    .dark .ca-id-copy { color:#90b4ff; }

    /* cost badge */
    .ca-cost-badge {
      display:inline-flex; align-items:center; gap:4px; padding:4px 10px;
      border-radius:999px; font-size:12px; font-weight:700;
      background:rgba(245,158,11,0.1); color:#d97706;
    }
    .dark .ca-cost-badge { background:rgba(245,158,11,0.15); color:#fbbf24; }

    .ca-muted { color:#6b7280; font-size:13px; }
    .dark .ca-muted { color:#7070a0; }

    /* action btns */
    .ca-btn-actions { display:flex; align-items:center; gap:6px; }
    .ca-action-btn {
      width:32px; height:32px; border-radius:8px; border:1.5px solid #e8eaf0;
      background:#f7f8fc; cursor:pointer; display:flex; align-items:center; justify-content:center;
      color:#7B829A; transition:all 0.15s;
    }
    .dark .ca-action-btn { background:#252540; border-color:#3a3a5a; color:#9099c0; }
    .ca-action-btn.copy:hover    { background:${ACCENT_SOFT}; border-color:${ACCENT}; color:${ACCENT}; }
    .ca-action-btn.restore:hover { background:rgba(22,163,74,0.1); border-color:#16a34a; color:#16a34a; }

    .ca-empty { text-align:center; padding:60px 20px !important; color:#7B829A; }
    .ca-empty p { margin:8px 0 0; font-size:14px; }
    .ca-spinner { width:32px; height:32px; border:3px solid #e8eaf0; border-top-color:${ACCENT}; border-radius:50%; animation:pm-spin 0.7s linear infinite; margin:0 auto 10px; }
    .ca-checkbox { width:16px; height:16px; cursor:pointer; accent-color:${ACCENT}; }

    /* ── Pagination ── */
    .ca-pagination { display:flex; align-items:center; justify-content:space-between; padding:12px 20px; flex-wrap:wrap; gap:10px; }
    .ca-pag-info { display:flex; align-items:center; gap:10px; font-size:13px; color:#7B829A; flex-wrap:wrap; }
    .dark .ca-pag-info { color:#6070a0; }
    .ca-pag-info strong { color:#1C2340; }
    .dark .ca-pag-info strong { color:#e0e0ff; }
    .ca-pag-select { border:1.5px solid #e0e2ef; border-radius:8px; padding:5px 8px; font-size:13px; font-family:'DM Sans',sans-serif; outline:none; background:#fff; color:#1C2340; cursor:pointer; }
    .dark .ca-pag-select { background:#252540; border-color:#3a3a5a; color:#e0e0ff; }
    .ca-pag-btns { display:flex; align-items:center; gap:4px; }
    .ca-pag-btn { min-width:34px; height:34px; border-radius:8px; border:1.5px solid #e0e2ef; background:#fff; color:#7B829A; font-size:13px; font-family:'DM Sans',sans-serif; cursor:pointer; padding:0 8px; font-weight:500; transition:all 0.15s; }
    .ca-pag-btn:hover:not(:disabled) { background:${ACCENT_SOFT}; border-color:${ACCENT}; color:${ACCENT}; }
    .ca-pag-btn.active { background:${ACCENT}; border-color:${ACCENT}; color:#fff; font-weight:700; }
    .ca-pag-btn:disabled { opacity:0.35; cursor:not-allowed; }
    .dark .ca-pag-btn { background:#252540; border-color:#3a3a5a; color:#7070a0; }
    .dark .ca-pag-btn:hover:not(:disabled) { background:rgba(13,71,161,0.25); border-color:#90b4ff; color:#90b4ff; }
    .dark .ca-pag-btn.active { background:${ACCENT}; border-color:${ACCENT}; color:#fff; }
    .ca-pag-ellipsis { padding:0 4px; color:#7B829A; font-size:14px; }

    /* ── Footer ── */
    .ca-footer { padding:16px 20px; font-size:12px; color:#9ca3af; border-top:1px solid #f0f2f8; }
    .dark .ca-footer { border-color:#2a2a45; }
  `;

  /* ── render ── */
  return (
    <>
      <style>{css}</style>

      <div
        className={`ca-shell ${isDark ? 'dark' : ''}`}
        style={{
          position:'fixed', top:'50px', left:sidebarWidth, right:0, bottom:0,
          transition:'left 0.25s cubic-bezier(0.4,0,0.2,1)',
          overflowY:'auto', overflowX:'hidden',
          display:'flex', flexDirection:'column',
          background: isDark ? '#111122' : '#f7f8fc',
        }}
      >

        {/* ─── Breadcrumb ─── */}
        <section style={{ padding:'16px 20px 0', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'14px' }}>
            <Link to={homeRoute} style={{ textDecoration:'none', color:isDark?'#9ca3af':'#6b7280', fontWeight:500 }}>Home</Link>
            <span style={{ color:isDark?'#4b5563':'#d1d5db' }}>/</span>
            <Link to="/Campaigns" style={{ textDecoration:'none', color:isDark?'#9ca3af':'#6b7280', fontWeight:500 }}>Campaigns</Link>
            <span style={{ color:isDark?'#4b5563':'#d1d5db' }}>/</span>
            <span style={{ color:isDark?'#e5e7eb':'#111827', fontWeight:600 }}>Trash</span>
          </div>
        </section>

        {/* ─── Stats ─── */}
        <section className="ca-stats" style={{ flexShrink:0 }}>
          {/* Total */}
          <div className="ca-stat-card light">
            <div>
              <div className="ca-stat-label">TOTAL IN TRASH</div>
              <div className="ca-stat-value">{allItems.length}</div>
              <div className="ca-stat-badge blue">↩ Restorable</div>
            </div>
            <div className="ca-stat-icon light"><IconMega /></div>
          </div>

          {/* Total Cost */}
          <div className="ca-stat-card light">
            <div>
              <div className="ca-stat-label">TOTAL COST</div>
              <div className="ca-stat-value large">
                {totalCost > 0
                  ? totalCost.toLocaleString('en-US', { style:'currency', currency:'USD', maximumFractionDigits:0 })
                  : '—'}
              </div>
              <div className="ca-stat-badge amber">Combined budget</div>
            </div>
            <div className="ca-stat-icon light" style={{ background:'rgba(245,158,11,0.1)', color:'#d97706' }}><IconCoin /></div>
          </div>

          {/* Filtered – dark card */}
          <div className="ca-stat-card dark-card">
            <div>
              <div className="ca-stat-label white">FILTERED RESULTS</div>
              <div className="ca-stat-value white">{filtered.length}</div>
              <div style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:'12px' }}>
                <span className="ca-badge-pill">SHOWING NOW</span>
                <span style={{ color:'rgba(255,255,255,0.6)', fontSize:'11px' }}>of {allItems.length} total</span>
              </div>
            </div>
            <div className="ca-stat-icon dark"><IconTrend /></div>
          </div>
        </section>

        {/* ─── Table Section ─── */}
        <section
          className="ca-table-section"
          style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minHeight:0 }}
        >
          {/* header */}
          <div className="ca-table-header" style={{ flexShrink:0 }}>
            <div>
              <div className="ca-title-label">MANAGEMENT</div>
              <div className="ca-title">CAMPAIGNS TRASH</div>
            </div>
            <div className="ca-actions">
              {/* Search */}
              <div className="ca-search">
                <IconSearch />
                <input
                  placeholder="Search by campaign name…"
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                />
              </div>

              {/* Bulk Restore */}
              {selectedItems.length > 0 && (
                <button className="ca-btn-restore" onClick={handleBulkRestore} disabled={restoreLoading}>
                  {restoreLoading
                    ? <><div style={{ width:14,height:14,border:'2px solid rgba(255,255,255,0.3)',borderTopColor:'#fff',borderRadius:'50%',animation:'pm-spin 0.7s linear infinite' }}/>Restoring…</>
                    : <><IconRestore /> Restore ({selectedItems.length})</>}
                </button>
              )}

              {/* Back */}
              <Link to="/Campaigns" style={{ textDecoration:'none' }}>
                <button className="ca-btn-primary">← All Campaigns</button>
              </Link>
            </div>
          </div>

          {/* Table */}
          <div className="ca-table-wrap" style={{ flex:1, overflowY:'auto', overflowX:'auto' }}>
            <table className="ca-table">
              <thead>
                <tr>
                  <th style={{ width:46 }}>
                    <input
                      type="checkbox" className="ca-checkbox"
                      checked={isAllSelected}
                      ref={el => { if (el) el.indeterminate = isSomeSelected; }}
                      onChange={handleSelectAll}
                    />
                  </th>
                  {['CAMPAIGN NAME', 'COST', 'START DATE', 'ACTIONS'].map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="ca-empty">
                      <div className="ca-spinner"/>
                      <span>Loading campaigns…</span>
                    </td>
                  </tr>
                ) : pageItems.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="ca-empty">
                      <IconSearch />
                      <p>No deleted campaigns found</p>
                    </td>
                  </tr>
                ) : pageItems.map(item => (
                  <tr
                    key={item._id}
                    className="ca-row"
                    style={{ background: selectedItems.includes(item._id) ? (isDark?'rgba(13,71,161,0.12)':'#eef3fb') : undefined }}
                  >
                    {/* Checkbox */}
                    <td>
                      <input
                        type="checkbox" className="ca-checkbox"
                        checked={selectedItems.includes(item._id)}
                        onChange={() => handleSelectOne(item._id)}
                      />
                    </td>

                    {/* Campaign Name */}
                    <td>
                      <div className="ca-name-cell">
                        <div className="ca-thumb">
                          {(item.CampainName || 'CA').slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div className="ca-name">{item.CampainName || '—'}</div>
                          <CopyToClipboard text={item._id} onCopy={() => { setCopyOpen(true); setTimeout(() => setCopyOpen(false), 2000); }}>
                            <span className="ca-id-copy" title="Copy ID">#{item._id?.slice(-6)}</span>
                          </CopyToClipboard>
                        </div>
                      </div>
                    </td>

                    {/* Cost */}
                    <td>
                      {item.Cost
                        ? <span className="ca-cost-badge">💰 {item.Cost}</span>
                        : <span className="ca-muted">—</span>}
                    </td>

                    {/* Start Date */}
                    <td>
                      {item.Date ? (
                        <div style={{ fontSize:13 }}>
                          <div style={{ fontWeight:600, color:isDark?'#c0c0e0':'#374151' }}>
                            {moment(item.Date).isValid()
                              ? moment(item.Date).format('MMM DD, YYYY')
                              : item.Date}
                          </div>
                        </div>
                      ) : <span className="ca-muted">—</span>}
                    </td>

                    {/* Actions */}
                    <td>
                      <div className="ca-btn-actions">
                        <CopyToClipboard text={item._id} onCopy={() => { setCopyOpen(true); setTimeout(() => setCopyOpen(false), 2000); }}>
                          <button className="ca-action-btn copy" title="Copy ID"><IconCopy /></button>
                        </CopyToClipboard>
                        <button
                          className="ca-action-btn restore"
                          title="Restore Campaign"
                          onClick={() => openConfirm(item)}
                        >
                          <IconRestore />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Pagination sticky ── */}
          {filtered.length > 0 && (
            <div
              className="ca-pagination"
              style={{ position:'sticky', bottom:0, zIndex:5, flexShrink:0, backgroundColor:isDark?'#1a1a2e':'#fff', borderTop:`1px solid ${isDark?'#2a2a45':'#e8eaf0'}`, boxShadow:'0 -2px 12px rgba(0,0,0,0.06)' }}
            >
              <div className="ca-pag-info">
                <span>Items per page:</span>
                <select className="ca-pag-select" value={itemsPerPage} onChange={e => { setItemsPerPage(Number(e.target.value)); setPage(1); }}>
                  {[5,10,25,50].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span>Showing <strong>{(page-1)*itemsPerPage+1}–{Math.min(page*itemsPerPage,filtered.length)}</strong> of <strong>{filtered.length}</strong> campaigns</span>
              </div>
              <div className="ca-pag-btns">
                <button className="ca-pag-btn" onClick={() => setPage(1)} disabled={page===1}>⟨|</button>
                <button className="ca-pag-btn" onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}>‹</button>
                {buildPageNumbers().map((n,i) =>
                  n === '...'
                    ? <span key={`e-${i}`} className="ca-pag-ellipsis">…</span>
                    : <button key={n} className={`ca-pag-btn ${page===n?'active':''}`} onClick={() => setPage(n)}>{n}</button>
                )}
                <button className="ca-pag-btn" onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}>›</button>
                <button className="ca-pag-btn" onClick={() => setPage(totalPages)} disabled={page===totalPages}>|⟩</button>
              </div>
            </div>
          )}
        </section>

        {/* Footer */}
        <footer className="ca-footer" style={{ flexShrink:0 }}>
          <span>© 2026 Realatix CRM. Smart CRM that connects customers, data, and sales in one safe place.</span>
        </footer>
      </div>

      {/* ─── Restore Confirm Modal ─── */}
      {confirmModal.open && (
        <PortalModal
          isDark={isDark}
          title="Restore Campaign?"
          subtitle={`"${confirmModal.name}" will be restored and become active again.`}
          onClose={() => setConfirmModal({ open:false, id:null, name:'' })}
          onConfirm={handleRestoreConfirm}
          confirmLabel={<><IconRestore /> Restore</>}
          confirmColor="#16a34a"
          loading={restoreLoading}
        />
      )}

      {/* ─── Copy Snackbar ─── */}
      <Snackbar open={copyOpen} autoHideDuration={2000} anchorOrigin={{ vertical:'bottom', horizontal:'center' }}>
        <Alert severity="success" sx={{ width:'100%' }}>Copied to clipboard!</Alert>
      </Snackbar>
    </>
  );
};
